/**
 * Push Notifications Initialization Service
 * Handles permission requests, token registration, and notification listeners
 * for iOS and Android using Capacitor + Firebase
 */

"use client";

import { authFetch } from "@/lib/api/auth-fetch";
import {
  isValidNotificationUrl,
  normalizeDeepLinkToAppPath,
} from "@/lib/notifications/navigation";

let isInitialized = false;
let tapListenerRegisteredEarly = false;
let tokenListeners: Array<(token: string | null) => void> = [];

/**
 * WHY THIS STATE IS EXPORTED AT ALL
 *
 * Push registration used to fail completely silently. If the OS permission was
 * ever refused, `requestPermissions()` returned "denied" for good (iOS only ever
 * asks once), this module returned early, and nothing anywhere in the app knew
 * or said so. The member simply never heard from GrowthSync again and had no way
 * to find out why. Measured 2026-09-10: not one push token had been registered
 * since 27 March, and every one of the owner's own accounts had none.
 *
 * So the outcome of the attempt is now recorded and readable, which is what lets
 * Settings show "notifications are off" and offer the way back.
 */
export type PushRegistrationState =
  | "unsupported"   // web, or no Capacitor bridge: nothing to register
  | "unknown"       // not attempted yet this session
  | "denied"        // OS permission refused; only the OS settings screen can undo it
  | "granted"       // permission held, token registered
  | "error";        // permission held but registration or token fetch failed

let registrationState: PushRegistrationState = "unknown";
let registrationStateListeners: Array<(state: PushRegistrationState) => void> = [];
/**
 * What actually went wrong, kept so it can be read rather than guessed at.
 *
 * The first version of this recorded only that registration had failed. The
 * settings screen then said "we could not set up notifications" and nobody,
 * including the people who wrote it, could tell you why. The cause turned out
 * to be a native contract this app was not honouring, and it cost a device log
 * to find. The message is now kept and shown to admins.
 */
let registrationError: string | null = null;

export function getPushRegistrationError(): string | null {
  return registrationError;
}

function setRegistrationState(next: PushRegistrationState, error?: unknown): void {
  registrationError =
    next === "error"
      ? (error instanceof Error ? error.message : error ? String(error) : "unknown error")
      : null;
  if (registrationState === next) return;
  registrationState = next;
  registrationStateListeners.forEach((listener) => {
    try {
      listener(next);
    } catch {
      /* a listener must not break registration */
    }
  });
}

export function getPushRegistrationState(): PushRegistrationState {
  return registrationState;
}

/** Subscribe to registration state changes. Returns an unsubscribe function. */
export function onPushRegistrationStateChange(
  callback: (state: PushRegistrationState) => void
): () => void {
  registrationStateListeners.push(callback);
  return () => {
    registrationStateListeners = registrationStateListeners.filter((l) => l !== callback);
  };
}

/**
 * Check if running on mobile platform (Capacitor)
 */
function isMobile(): boolean {
  if (typeof window === "undefined") return false;
  const Capacitor = (window as any).Capacitor;
  if (!Capacitor) return false;
  // Check that platform is actually iOS or Android, not web
  const platform = Capacitor.getPlatform?.() || "web";
  return platform === "ios" || platform === "android";
}

/**
 * Get current platform (ios, android, web)
 */
function getPlatform(): string {
  if (typeof window === "undefined") return "web";
  const Capacitor = (window as any).Capacitor;
  if (!Capacitor) return "web";
  return Capacitor.getPlatform() || "web";
}

/**
 * Extracts deep link URL from notification payload.
 * Handles multiple payload shapes (FCM, APNs, Capacitor) and iOS cold start
 * where data may be missing or nested differently.
 */
function extractDeepLinkUrl(notification: { notification?: { data?: Record<string, unknown>; [k: string]: unknown }; [k: string]: unknown }): string | null {
  // Primary: notification.notification.data.url (Capacitor ActionPerformed structure)
  const data = notification.notification?.data ?? notification.notification ?? notification;
  const dataObj = data && typeof data === "object" ? (data as Record<string, unknown>) : {};
  let rawUrl = typeof dataObj?.url === "string" ? dataObj.url.trim() : null;
  if (rawUrl) {
    const normalizedFromUrl = normalizeDeepLinkToAppPath(rawUrl);
    if (normalizedFromUrl) {
      rawUrl = normalizedFromUrl;
    }
  }
  // iOS sometimes delivers /events/{id} without ?action=review even when that was the target;
  // merge eventAction from FCM data when the path has no action query.
  const eventActionRaw =
    typeof dataObj?.eventAction === "string" ? String(dataObj.eventAction).trim() : "";
  const eventAction =
    eventActionRaw === "review" || eventActionRaw === "reviews" ? eventActionRaw : "";
  if (rawUrl && eventAction) {
    try {
      const [pathPart] = rawUrl.split("?");
      const cleanPath = pathPart.split("#")[0];
      if (/^\/events\/[^/]+$/.test(cleanPath) && !rawUrl.includes("action=")) {
        rawUrl = `${cleanPath}?action=${eventAction}`;
      }
    } catch {
      /* ignore */
    }
  }
  // Fallbacks for iOS cold start when url may be omitted but entity IDs are present
  if (!rawUrl && typeof dataObj?.matchId === "string") {
    const matchId = String(dataObj.matchId).trim();
    if (matchId) rawUrl = `/messages/${matchId}`;
  }
  if (!rawUrl && typeof dataObj?.eventId === "string") {
    const eventId = String(dataObj.eventId).trim();
    if (eventId) {
      rawUrl =
        eventAction !== ""
          ? `/events/${eventId}?action=${eventAction}`
          : `/events/${eventId}`;
    }
  }
  if (!rawUrl && typeof dataObj?.opportunityId === "string") {
    const opportunityId = String(dataObj.opportunityId).trim();
    if (opportunityId) rawUrl = `/opportunities/${opportunityId}`;
  }
  if (!rawUrl && typeof dataObj?.communityId === "string") {
    const communityId = String(dataObj.communityId).trim();
    if (communityId) rawUrl = `/communities/${communityId}`;
  }
  if (!rawUrl && typeof dataObj?.serviceId === "string") {
    const serviceId = String(dataObj.serviceId).trim();
    if (serviceId) rawUrl = `/services/${serviceId}/manage`;
  }
  if (!rawUrl && typeof dataObj?.likerProfileId === "string" && dataObj?.type === "match_new") {
    const profileId = String(dataObj.likerProfileId).trim();
    if (profileId) rawUrl = `/discover?tab=likes-you&profile=${profileId}`;
  }
  // Also check nested data (some FCM/APNs payloads nest data)
  if (!rawUrl && dataObj?.data && typeof dataObj.data === "object") {
    const nested = dataObj.data as Record<string, unknown>;
    rawUrl = typeof nested?.url === "string" ? nested.url.trim() : null;
    if (rawUrl) {
      const nestedNorm = normalizeDeepLinkToAppPath(rawUrl);
      if (nestedNorm) rawUrl = nestedNorm;
    }
    const nestedActionRaw =
      typeof nested?.eventAction === "string" ? String(nested.eventAction).trim() : "";
    const nestedAction =
      nestedActionRaw === "review" || nestedActionRaw === "reviews" ? nestedActionRaw : "";
    if (rawUrl && nestedAction) {
      try {
        const [pathPart] = rawUrl.split("?");
        const cleanPath = pathPart.split("#")[0];
        if (/^\/events\/[^/]+$/.test(cleanPath) && !rawUrl.includes("action=")) {
          rawUrl = `${cleanPath}?action=${nestedAction}`;
        }
      } catch {
        /* ignore */
      }
    }
    if (!rawUrl && typeof nested?.matchId === "string") {
      const matchId = String(nested.matchId).trim();
      if (matchId) rawUrl = `/messages/${matchId}`;
    }
    if (!rawUrl && typeof nested?.eventId === "string") {
      const eventId = String(nested.eventId).trim();
      if (eventId) {
        rawUrl =
          nestedAction !== ""
            ? `/events/${eventId}?action=${nestedAction}`
            : `/events/${eventId}`;
      }
    }
  }
  // Legacy payloads may still point to /admin/events/{id}; normalize to the
  // canonical event detail route expected by the app.
  if (rawUrl) {
    const adminEventMatch = rawUrl.match(/^\/admin\/events\/([^/?]+)/);
    if (adminEventMatch?.[1]) {
      rawUrl = `/events/${adminEventMatch[1]}`;
    }
  }
  // Invalid url (e.g. mangled absolute URL) must not block matchId/eventId fallbacks — those only ran when url was absent.
  if (rawUrl && !isValidNotificationUrl(rawUrl)) {
    rawUrl = null;
  }
  if (!rawUrl && typeof dataObj?.matchId === "string") {
    const matchId = String(dataObj.matchId).trim();
    if (matchId) rawUrl = `/messages/${matchId}`;
  }
  if (!rawUrl && typeof dataObj?.eventId === "string") {
    const eventId = String(dataObj.eventId).trim();
    if (eventId) {
      rawUrl =
        eventAction !== ""
          ? `/events/${eventId}?action=${eventAction}`
          : `/events/${eventId}`;
    }
  }
  if (!rawUrl && dataObj?.data && typeof dataObj.data === "object") {
    const nested = dataObj.data as Record<string, unknown>;
    if (typeof nested?.matchId === "string") {
      const matchId = String(nested.matchId).trim();
      if (matchId) rawUrl = `/messages/${matchId}`;
    }
    if (!rawUrl && typeof nested?.eventId === "string") {
      const nestedActionRaw =
        typeof nested?.eventAction === "string" ? String(nested.eventAction).trim() : "";
      const nestedAction =
        nestedActionRaw === "review" || nestedActionRaw === "reviews" ? nestedActionRaw : "";
      const eventId = String(nested.eventId).trim();
      if (eventId) {
        rawUrl =
          nestedAction !== ""
            ? `/events/${eventId}?action=${nestedAction}`
            : `/events/${eventId}`;
      }
    }
  }
  return rawUrl || null;
}

/**
 * Handles push notification tap - stores redirect, pre-fetches, dispatches.
 * Shared between early registration and full init.
 */
async function handlePushNotificationTap(notification: { notification?: { data?: Record<string, unknown> } }): Promise<void> {
  const rawUrl = extractDeepLinkUrl(notification);
  if (typeof window === "undefined") return;

  const { getSafeDeepLinkUrl } = await import("@/lib/notifications/navigation");
  const deepLinkUrl = getSafeDeepLinkUrl(rawUrl) || "/notifications";

  // Message threads need Supabase session before GET /api/messages — cold start used to navigate first
  // and briefly show "conversation no longer available" / 401 until session boot finished.
  if (deepLinkUrl.startsWith("/messages/")) {
    try {
      const { sessionRestorePromise } = await import("@/lib/supabase/client");
      await sessionRestorePromise;
    } catch (e) {
      console.warn(`[Push Notifications] Session restoration before message deeplink:`, e);
    }
  }

  // CRITICAL: Store redirect URL for auth flow - ensures post-login redirect goes to message thread, not dashboard
  const { storeRedirectUrl } = await import("@/lib/utils/redirect-storage");
  storeRedirectUrl(deepLinkUrl);

  // Navigate after session is ready for message links; other links stay snappy.
  const { dispatchPushDeeplink } = await import("@/components/push-notification-deeplink-handler");
  dispatchPushDeeplink(deepLinkUrl);

  // Warm match/message data in the background without blocking navigation.
  void (async () => {
  try {
    const { sessionRestorePromise } = await import("@/lib/supabase/client");
    await sessionRestorePromise;
  } catch (e) {
    console.warn(`[Push Notifications] Session restoration (non-blocking):`, e);
  }

  const isMatchOrMessageLink = deepLinkUrl.startsWith("/messages/");
  if (isMatchOrMessageLink) {
    try {
      const { useMatchStore } = await import("@/stores/match-store");
      const { useMessageStore } = await import("@/stores/message-store");
      const matchIdMatch = deepLinkUrl.match(/\/messages\/([^/?]+)/);
      const matchId = matchIdMatch ? matchIdMatch[1] : null;
      if (matchId) {
        const matchStore = useMatchStore.getState();
        if (matchStore.fetchMatches) await matchStore.fetchMatches().catch(() => {});
        const messageStore = useMessageStore.getState();
        if (messageStore.fetchMessages) await messageStore.fetchMessages(matchId).catch(() => {});
      }
    } catch {
      /* non-blocking */
    }
  }
  })();
}

/**
 * Registers ONLY the push tap listener - no permissions or token registration.
 * Call from root layout on mobile to capture notification taps on cold start
 * (before user reaches app layout where full init runs).
 */
export async function registerPushTapListenerEarly(): Promise<void> {
  if (tapListenerRegisteredEarly || typeof window === "undefined") return;
  const Capacitor = (window as any).Capacitor;
  if (!Capacitor) return;
  const platform = Capacitor.getPlatform?.() || "web";
  if (platform !== "ios" && platform !== "android") return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const listener = await PushNotifications.addListener("pushNotificationActionPerformed", async (notification) => {
      await handlePushNotificationTap(notification);
    });
    tapListenerRegisteredEarly = true;
    (window as any).__pushTapListenerEarly = listener;
  } catch {
    // Non-fatal - early tap listener not available
  }
}

/**
 * Initialize push notifications
 * Requests permission, registers token, sets up listeners
 */
export async function initializePushNotifications(): Promise<void> {
  // Only initialize once
  if (isInitialized) return;

  // Only run on mobile
  if (!isMobile()) {
    setRegistrationState("unsupported");
    return;
  }

  const platform = getPlatform();

  // Only proceed on iOS or Android
  if (platform !== "ios" && platform !== "android") {
    setRegistrationState("unsupported");
    return;
  }

  try {
    // Dynamically import Capacitor plugins
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");

    // Ask what we already hold before asking the member anything. On iOS the
    // system prompt appears exactly once in the app's lifetime, so calling
    // requestPermissions() on an account that refused months ago just returns
    // "denied" again and burns nothing; what matters is that we now record it
    // rather than returning into silence.
    const current = await PushNotifications.checkPermissions().catch(() => null);

    if (current?.receive === "denied") {
      setRegistrationState("denied");
      return;
    }

    const permissionResult =
      current?.receive === "granted"
        ? current
        : await PushNotifications.requestPermissions();

    if (permissionResult.receive !== "granted") {
      setRegistrationState(permissionResult.receive === "denied" ? "denied" : "unknown");
      return;
    }

    // Register for push notifications
    await PushNotifications.register();

    // Listen for registration (APNs token on iOS, direct registration on Android)
    const registrationListener = await PushNotifications.addListener("registration", async () => {
      try {
        const tokenResult = await FirebaseMessaging.getToken();
        if (tokenResult.token) {
          await saveTokenToBackend(tokenResult.token, platform);
          tokenListeners.forEach(listener => listener(tokenResult.token));
        }
      } catch (error) {
        console.error(`[Push Notifications] Error getting FCM token after registration:`, error);
        reportPushFailure("getToken after registration", error);
      }
    });

    // Listen for registration errors
    const registrationErrorListener = await PushNotifications.addListener("registrationError", (error) => {
      console.error(`[Push Notifications] Registration error on ${platform}:`, error);
      // Notify listeners of error
      tokenListeners.forEach(listener => listener(null));
    });

    // Listen for push notification received (foreground)
    const pushReceivedListener = await PushNotifications.addListener("pushNotificationReceived", (_notification) => {
      // Handle foreground notification (e.g., show in-app notification)
    });

    // Listen for push notification action performed (tapped/opened)
    // Skip if early tap listener already registered (avoids duplicate handling)
    const pushActionListener = tapListenerRegisteredEarly
      ? null
      : await PushNotifications.addListener("pushNotificationActionPerformed", async (notification) => {
          await handlePushNotificationTap(notification);
        });

    /*
     * Everything past this point is wrapped individually. It used to be one
     * unbroken run of awaits inside a single try, so the first call that threw
     * abandoned the rest, including the two paths that actually save a token.
     * A token refresh listener failing is not a reason to stop trying to fetch
     * a token.
     */
    let tokenRefreshListener: { remove: () => Promise<void> } | null = null;
    try {
      tokenRefreshListener = await FirebaseMessaging.addListener("tokenReceived", async (event) => {
        if (event.token) {
          await saveTokenToBackend(event.token, platform);
          tokenListeners.forEach((listener) => listener(event.token));
        }
      });
    } catch (error) {
      console.error("[Push Notifications] Could not listen for token refresh:", error);
      reportPushFailure("addListener tokenReceived", error);
    }

    // Now ask the OS to register, with every listener already in place.
    let registered = false;
    try {
      await PushNotifications.register();
      registered = true;
    } catch (error) {
      console.error("[Push Notifications] register() failed:", error);
      reportPushFailure("register", error);
    }

    // Ask for a token directly as well. On Android one is usually available
    // straight away; on iOS this is the second chance if the registration event
    // is missed.
    let haveToken = false;
    try {
      const tokenResult = await FirebaseMessaging.getToken();
      if (tokenResult.token) {
        await saveTokenToBackend(tokenResult.token, platform);
        tokenListeners.forEach((listener) => listener(tokenResult.token));
        haveToken = true;
      }
    } catch (error) {
      // Expected on iOS before APNs has answered: the registration listener
      // above is what picks it up. Only worth reporting if nothing else worked.
      if (!registered) reportPushFailure("getToken", error);
    }

    isInitialized = true;
    setRegistrationState(registered || haveToken ? "granted" : "error", registrationError);


    // Store listeners for cleanup (if needed) - pushActionListener may be null if early handler registered
    (window as any).__pushNotificationListeners = [
      registrationListener,
      registrationErrorListener,
      pushReceivedListener,
      ...(pushActionListener ? [pushActionListener] : []),
      tokenRefreshListener,
    ];
  } catch (error) {
    console.error("[Push Notifications] Initialization error:", error);
    reportPushFailure("initialize", error);
    setRegistrationState("error", error);
  }
}

/**
 * Push failing is invisible by nature: nothing arrives, and the member assumes
 * nobody has been in touch. It stayed broken for five and a half months for
 * exactly that reason, so every failure now reaches Sentry with the step that
 * produced it, and the message is kept for the settings screen.
 */
function reportPushFailure(step: string, error: unknown): void {
  registrationError = `${step}: ${error instanceof Error ? error.message : String(error)}`;
  void (async () => {
    try {
      const Sentry = await import("@sentry/nextjs");
      Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
        tags: { area: "push-notifications", step },
      });
    } catch {
      // Sentry unavailable: the console line above is all we get.
    }
  })();
}

/**
 * Re-attempt registration after the member has been to the OS settings screen.
 *
 * Without this, turning notifications back on in iOS or Android settings did
 * nothing until the app was reinstalled: init had already run, `isInitialized`
 * or the denied state kept it from running again, and no token was ever sent.
 * Called on app resume so the fix takes effect the moment they come back.
 */
export async function refreshPushRegistration(): Promise<PushRegistrationState> {
  if (!isMobile()) {
    setRegistrationState("unsupported");
    return registrationState;
  }

  // Already holding a token: nothing to redo.
  if (isInitialized && registrationState === "granted") return registrationState;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const current = await PushNotifications.checkPermissions().catch(() => null);
    if (current?.receive !== "granted") {
      setRegistrationState(current?.receive === "denied" ? "denied" : "unknown");
      return registrationState;
    }
  } catch {
    setRegistrationState("error");
    return registrationState;
  }

  // Permission is held now even though a previous attempt gave up, so run the
  // full initialisation again from a clean slate.
  isInitialized = false;
  await initializePushNotifications();
  return registrationState;
}

/**
 * Save push token to backend
 */
async function saveTokenToBackend(token: string, platform: string): Promise<void> {
  try {
    // `platform` was accepted here and then dropped from the body, which is why
    // every row in push_tokens carries platform NULL and nothing can tell an
    // iPhone from an Android phone when a send fails.
    const response = await authFetch("/api/push-token", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, platform }),
    });

    if (!response.ok) {
      console.error(`[Push Notifications] Failed to save token to backend:`, response.status, response.statusText);
      return;
    }

    await response.json().catch(() => ({}));
  } catch (error) {
    console.error(`[Push Notifications] Error saving token to backend:`, error);
  }
}

/**
 * Subscribe to token updates
 * Returns unsubscribe function
 */
export function onTokenUpdate(callback: (token: string | null) => void): () => void {
  tokenListeners.push(callback);
  return () => {
    tokenListeners = tokenListeners.filter(listener => listener !== callback);
  };
}

/**
 * Cleanup all listeners (if needed)
 */
export async function cleanupPushNotifications(): Promise<void> {
  if (!isMobile() || !(window as any).__pushNotificationListeners) {
    return;
  }

  try {
    const listeners = (window as any).__pushNotificationListeners || [];
    for (const listener of listeners) {
      if (listener && typeof listener.remove === "function") {
        await listener.remove();
      }
    }
    (window as any).__pushNotificationListeners = [];
    isInitialized = false;
  } catch (error) {
    console.error("[Push Notifications] Cleanup error:", error);
  }
}

